import React, { useMemo } from 'react';
import { Box, Typography, Grid, IconButton, Tooltip } from '@mui/material';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttStatCard from '../LeaveAttendanceComps/AttStatCard';
import { card, th, td, EmptyState } from '../uiKit';
import { inr } from '../../utils/format';
import PaymentLedger from './PaymentLedger';

const sum = (list) => list.reduce((s, r) => s + r.amount, 0);

/**
 * Drill-down for one category (any Expenses tab) or one employee (Shoot). An employee on a
 * payment-tracking tab opens straight onto their payment history; a category shows its entries.
 * `value` is the category name or the employee code; `title` overrides the heading.
 * `columns` are the board's table columns without the actions column.
 */
export default function ExpenseDetail({ config, kind, value, title, rows, columns, onOpenEntry, onBack, onRemovePayment, onRemoveExpense, refreshKey }) {
    const historyOnly = config.payTiming && kind === 'employee';
    const subset = useMemo(() => rows.filter((r) => (
        kind === 'category' ? r.category === value : (r.employeeCodes || []).includes(value)
    )), [rows, kind, value]);
    const cols = columns.map((c) => c.w).join(' ');

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                {onBack && (
                    <Tooltip arrow title="Back to payment history">
                        <IconButton onClick={onBack} aria-label="Back to payment history" sx={{ border: '1px solid #E6EAF1', borderRadius: '8px', bgcolor: '#fff', color: '#475569', '&:hover': { bgcolor: '#F1EEFE', color: '#6B58D6' } }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{title || value}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                        {kind === 'category' ? 'Category' : 'Employee'} · {config.noun} expenses
                    </Typography>
                </Box>
            </Box>

            {historyOnly ? (
                <PaymentLedger code={value} onOpenEntry={onOpenEntry} onRemoveExpense={onRemoveExpense} onRemovePayment={onRemovePayment} refreshKey={refreshKey} />
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                        <Grid size={{ xs: 12, sm: 6 }}><AttStatCard label="Total" value={inr(sum(subset))} icon={PaymentsRoundedIcon} tone="purple" /></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}><AttStatCard label="Entries" value={subset.length} icon={ReceiptLongRoundedIcon} tone="blue" /></Grid>
                    </Grid>
                    <Box sx={{ ...card, overflow: 'hidden' }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Box sx={{ minWidth: 760 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: cols, bgcolor: '#F7F6FD' }}>
                                    {columns.map((c, i) => <Box key={`${c.label}${i}`} sx={th}>{c.label}</Box>)}
                                </Box>
                                {subset.length === 0 ? (
                                    <EmptyState icon={ReceiptLongRoundedIcon} title="No matching expenses" hint="Nothing recorded here yet." />
                                ) : subset.map((r) => (
                                    <Box key={r.id} onClick={() => onOpenEntry(r.id)} sx={{ display: 'grid', gridTemplateColumns: cols, cursor: 'pointer', borderBottom: '1px solid #EEF0F6', '&:last-of-type': { borderBottom: 'none' }, '&:hover': { bgcolor: '#FAFAFE' } }}>
                                        {columns.map((c, i) => <Box key={i} sx={{ ...td, borderBottom: 'none', display: 'flex', alignItems: 'center', minWidth: 0 }}>{c.render(r)}</Box>)}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
}
